import CircularProgress from '@material-ui/core/CircularProgress';
import {ThemeProvider} from '@material-ui/core/styles';
import {detect} from 'detect-browser';
import React, {useEffect, useState} from 'react';
import {BrowserRouter as Router, Route, Switch, useLocation} from 'react-router-dom';
import {retrieveAccount} from './account';
import {loadClientInfo} from './clientInfo';
import ActivityCenter from './components/ActivityCenter/ActivityCenter';
import CaseDetails from './components/ActivityCenter/SubComponents/CaseDetails';
import ResourceCenter from './components/Resource Center/ResourceCenter';
import StateDisclosure from './components/Disclosure/StateDisclosure';
import Products from './components/Helpers/Data/Products/Products';
import NoIeSupport from './components/Helpers/Errors/Templates/NoIeSupport';
import AdvisorWelcome from './components/Home/AdvisorWelcome';
import Layout from './components/Layout/Layout';
import {LAYOUT_DEFAULT_NAV_LINKS, setDefaultLinks} from './components/Layout/LayoutConst';
import NewSession from './components/NewSession';
import PolicyReview from './components/PolicyReview/PolicyReview';
import AssetBasedLTC from './components/Product/ABLTC/RequestIllustration/AssetBasedLTC';
import TermLifeInsurance from './components/Product/TermLifeInsurance/TermLifeInsurance';
import ProductPage from './components/ProductLanding/Product';
import ProductsPage from './components/ProductLanding/Products';
import InsuranceTools from './components/InsuranceTools/InsuranceTools';
import NeedsAnalysis from './components/InsuranceTools/NeedsAnalysis/NeedsAnalysis';
import Ineligible from './components/Quoters/Common/Ineligible';
import Theme from './components/Theme/Theme';
import {saveSessionEvent} from './session';
import SessionTimeout from './SessionTimeout';
import Notifications from './components/Notifications/Notifications';
import TermReview from './components/PolicyReview/TermReview/TermReview';
import CreateCase from "./components/Product/CreateCase/CreateCase";
import {
  ADVISOR_OBJECT_KEY,
  getSessionStorageItem,
  SERVICES_BASE_URL_KEY,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_CLIENTINFO_KEY,
  SESSION_STORAGE_IS_SYSTEM_OUTAGE_ALERT,
  setSessionStorageItem,
  setSessionStorageItemWithExpiration
} from './sessionStorage';
import PermReview from './components/PolicyReview/PermReview/PermReview';
import PermLifeInsurance from './components/Product/PermLifeInsurance/PermLifeInsurance';
import {UserInfoProvider} from "./providers/UserProvider";
import AuthorizedRoute from "./routes/routeTypes/AuthorizedRoute";
import PermissionTypes from "./constants/PermissionTypes";
import { Alert, AlertTitle } from '@material-ui/lab';
import { Col, Row } from 'react-bootstrap';

const browser = detect();
var isIe = browser.name === 'ie';

const PageNotFound = () => {
  return (
    <div className="container">
      <div className="jumbotron mt-4">Page not found.</div>
    </div>
  );
};

let primaryColor = null;
let secondaryColor = null;

const setColorsFromAccount = (acc) => {
  primaryColor = acc ? acc.primaryColor : null;
  secondaryColor = acc ? acc.secondaryColor : null;
};

const App = () => {
  const [loadingSite, setLoadingSite] = useState(true);
  const [loadingClientInfo, setLoadingClientInfo] = useState(true);
  const [error, setError] = useState(false);
  const [user, setUser] = useState();
  const [invalidSignOn, setInvalidSignOn] = useState(false);
  const [account, setAccount] = useState();
  const [navLinks, setActiveNavLink] = useState(LAYOUT_DEFAULT_NAV_LINKS);
  const [technicalErrorMessage, setTechnicalErrorMessage] = useState('');
  const isTimeOut = window.location.href.includes("SessionTimeOut");
  const technicalErrorForUserEntitlements = "userEntitlementsErrors";
  const isSystemOutageAlertEnable = getSessionStorageItem(SESSION_STORAGE_IS_SYSTEM_OUTAGE_ALERT);
  const [isShowSystemOutageAlert, setIsShowSystemOutageAlert] = useState(isSystemOutageAlertEnable);
  const messageOnPlatform = account && account.account && account.account.messageOnPlatform;

  const setActiveLink = (currentIndex) => {
    let navItems = navLinks;
    navItems.forEach((part, index, array) => {
      array[index].isActive = false;
    });
    navItems[currentIndex].isActive = true;

    setActiveNavLink([...navItems]);
  }

  const extendSession = (data) => {
    const userInfoFromSession = getSessionStorageItem(ADVISOR_OBJECT_KEY);

    const userInfo = {...userInfoFromSession};
    userInfo.userToken = data.userToken;

    setSessionStorageItem(ADVISOR_OBJECT_KEY, userInfo);
  }

  let location = useLocation();
  useEffect(() => {
    saveSessionEvent();

    let navItems = setDefaultLinks();
    setActiveNavLink([...navItems]);
  }, [location]);

  useEffect(() => {
    var accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);

    if (!accountObj) {
      retrieveAccount()
        .then((account) => {
          if (!account.accountProducts.length) {
            account.accountProducts = Products();
          }
          setSessionStorageItemWithExpiration(SESSION_STORAGE_ACCOUNT_KEY, account, 60);
          setColorsFromAccount(account.account);
          setAccount(account);
          if (account && account.account && account.account.messageOnPlatform) {
            setSessionStorageItem(SESSION_STORAGE_IS_SYSTEM_OUTAGE_ALERT, true);
            setIsShowSystemOutageAlert(true)
          } 
          else {
            setSessionStorageItem(SESSION_STORAGE_IS_SYSTEM_OUTAGE_ALERT, false);
            setIsShowSystemOutageAlert(false)
          }
        })
        .catch((error) => {
          setError(true);
          setLoadingSite(false);
          return;
        });
    } else {
      setColorsFromAccount(accountObj.account);
      setAccount(accountObj);
    }
  }, []);

  useEffect(() => {

    if (!account) {
      return;
    }

    const sessionId = account.sessionId;

    const getUser = () => {
      fetch(`/SSO/GetUserInfo?sessionId=${sessionId}`, {
        method: 'GET'
      })
        .then(res => {
          if (!res.ok) {
            throw new Error('Agent not found');
          } else {
            return res.json()
          }
        })
        .then(data => {

          if (!data) {
            setInvalidSignOn(true);
            setLoadingSite(false);
          } else if (data && data.message) {
            setInvalidSignOn(true);
            setLoadingSite(false);
            if (data.paramName === technicalErrorForUserEntitlements) {
              setTechnicalErrorMessage(data.message);
            }  
          } else {

            const userInfo = {
              userToken: data.userToken,
              fullName: data.fullName,
              iscName: data.iscName,
              firstTimeLogin: data.firstTimeLogin,
              premiumGoal: data.premiumGoal,
              appGoal: data.appGoal,
              role: data.role,
              features: data.features || {},
              ricEmail: data.ricEmail,
              ricWorkPhone: data.ricWorkPhone,
              ricBiography: data.ricBiography,
              ricCalendlyURL: data.ricCalendlyURL,
              ricProfileImageURL: data.ricProfileImageURL,
              aipProfileImageURL: data.aipProfileImageURL,
              sessionTimeout: data.sessionTimeout,
              assignedUnderwritingBio: data.assignedUnderwritingBio,
              assignedUnderwritingCalendlyURL: data.assignedUnderwritingCalendlyURL,
              assignedUnderwritingEmail: data.assignedUnderwritingEmail,
              assignedUnderwritingFullName: data.assignedUnderwritingFullName,
              assignedUnderwritingProfileImageURL: data.assignedUnderwritingProfileImageURL,
              assignedUnderwritingWorkPhone: data.assignedUnderwritingWorkPhone,
              externalUserId: data.externalUserId,
              permissions: data.permissions,
              faOptions: data.faOptions,
              insuranceSpecialistURL: data.insuranceSpecialistURL,
              unviewedNotificationCount : data.unviewedNotificationCount
            };

            setSessionStorageItem(ADVISOR_OBJECT_KEY, userInfo);
            setSessionStorageItem(SERVICES_BASE_URL_KEY, data.servicesBaseURL);

            setUser(userInfo);
            setLoadingSite(false);
          }
        }, () => {
          setInvalidSignOn(true);
          setLoadingSite(false);
        });
    }

    const importClientData = () => {
      setLoadingClientInfo(true);
      loadClientInfo().then((clientInfo) => {
        if (clientInfo !== undefined) {
          setSessionStorageItemWithExpiration(SESSION_STORAGE_CLIENTINFO_KEY, clientInfo, 60);
        }
        setLoadingClientInfo(false);
      }).catch((error) => {
        setLoadingClientInfo(false);
      });
    }

    let advisorObj = getSessionStorageItem(ADVISOR_OBJECT_KEY);

    if (!advisorObj) {
      getUser();
    } else {
      setUser(advisorObj)
      setLoadingSite(false);
    }

    if (window.location.href.includes("DataImport")) {
      importClientData();
    } else {
      setLoadingClientInfo(false);
    }
  }, [account]);

  const handleSystemAlertBoxClose = () => {
    setSessionStorageItem(SESSION_STORAGE_IS_SYSTEM_OUTAGE_ALERT, false);
    setIsShowSystemOutageAlert(false);
  }

  if (isIe) {
    return (
      <div>
        <NoIeSupport/>
      </div>
    );
  }

  return (
    <React.Fragment>
      {loadingSite === false && loadingClientInfo === false && error === false && invalidSignOn === false && (
        <ThemeProvider theme={Theme.get(primaryColor, secondaryColor)}>
          <UserInfoProvider userInfo={user}>
            <Router>
              <Layout
                navLinks={navLinks}
                logoURL={process.env.PUBLIC_URL + '/AdvisorImages/Morgan_Stanley.png'}
                setActiveNavLink={setActiveLink}
              >
                <Switch>
                  <Route path="/" exact={true} component={(props) => <AdvisorWelcome {...props} />}/>

                  <Route path="/Quote/TermReview" component={(props) => <TermReview {...props} />}/>
                  <Route path="/Quote/TermReview/:path" component={(props) => <TermReview {...props} />}/>

                  <Route path="/Quote/PermReview" component={(props) => <PermReview {...props} />}/>
                  <Route path="/Quote/PermReview/:path" component={(props) => <PermReview {...props} />}/>

                  <Route path="/ResourceCenter" component={(props) => <ResourceCenter {...props} />}/>

                  <Route path="/InsuranceTools/NeedsAnalysis" component={(props) => <NeedsAnalysis {...props} />}/>
                  <Route path="/InsuranceTools" component={(props) => <InsuranceTools {...props} />}/>

                  <AuthorizedRoute path="/Notifications"
                                   component={(props) => <Notifications {...props} setActiveLink={setActiveLink} navLinks={navLinks}/>} permission={PermissionTypes.VIEW_CASE_STATUS}/>

                  <AuthorizedRoute path="/Activity/NewCase"
                    component={(props) => <CreateCase {...props} roleType={user && user.role} />} permission={PermissionTypes.RUN_QUOTE}/>
                  <AuthorizedRoute path="/Activity/NewCase/:path"
                    component={(props) => <CreateCase {...props} />} permission={PermissionTypes.RUN_QUOTE}/>

                  <AuthorizedRoute path="/ActivityCenter/:referenceID"
                                   component={(props) => <CaseDetails {...props} />} permission={PermissionTypes.VIEW_CASE_STATUS} />
                  <AuthorizedRoute path="/ActivityCenter"
                                   component={(props) => <ActivityCenter {...props} />} permission={PermissionTypes.VIEW_CASE_STATUS}/>

                  <AuthorizedRoute path="/Products/ABLTC"
                                   component={(props) => <AssetBasedLTC {...props} />} permission={PermissionTypes.RUN_QUOTE}/>
                  <AuthorizedRoute path="/Products/ABLTC/:path"
                                   component={(props) => <AssetBasedLTC {...props} />} permission={PermissionTypes.RUN_QUOTE}/>

                  <AuthorizedRoute path="/Products/Term"
                                   component={(props) => <TermLifeInsurance {...props} roleType={user.role} />} permission={PermissionTypes.RUN_QUOTE}/>
                  <AuthorizedRoute path="/Products/Term/:path"
                                   component={(props) => <TermLifeInsurance {...props} />} permission={PermissionTypes.RUN_QUOTE}/>

                  <AuthorizedRoute path="/Products/Perm"
                                   component={(props) => <PermLifeInsurance {...props} />} permission={PermissionTypes.RUN_QUOTE}/>
                  <AuthorizedRoute path="/Products/Perm/:path"
                                   component={(props) => <PermLifeInsurance {...props} />} permission={PermissionTypes.RUN_QUOTE}/>

                  <Route path="/Products/Landing/:product" component={(props) => <ProductPage {...props} />}/>
                  <Route path="/Products" component={(props) => <ProductsPage {...props} />}/>

                  <Route path="/Ineligible" component={Ineligible}/>
                  <Route path="/StateDisclosure" component={StateDisclosure}/>

                  <Route path="/PolicyReview" component={PolicyReview}/>
                  <Route path="/ns" component={() => <NewSession setUser={setUser}/>}/>
                  <Route path="/logOut" component={() => <NewSession setUser={setUser} isSessionTimeout={true} />} />
                  <Route path="*" component={PageNotFound}/>
                </Switch>
              </Layout>
            </Router>
          </UserInfoProvider>
        </ThemeProvider>
      )}

      {loadingSite && (
        <div className="d-flex justify-content-center mt-4">
          <CircularProgress/>
        </div>
      )}

      {error && (
        <div className="d-flex justify-content-center mt-4">
          <p>The account you are trying to access does not exist. Please check the URL and try again.</p>
        </div>
      )}

      {invalidSignOn && (
        <React.Fragment>
          <div className="d-flex justify-content-center mt-4">
            {isTimeOut ? <p>Your current session has timed out. Please re-launch the application via 3D {`>`} Solutions {`>`} Annuities & Insurance {`>`} Insurance Platform.</p> :
              <p>Invalid sign on request. Please call <a href={`tel:${(account && account.account && account.account.phone) || 111 - 222 - 4444}`}> {(account && account.account.phone) || '111-222-4444'}</a> for assistance.</p>
            }
          </div>
          {(technicalErrorMessage !== '') && <Alert severity="info" className="d-flex justify-content-center ml-5 mr-5">{technicalErrorMessage}</Alert>}
        </React.Fragment>
      )}

      {isShowSystemOutageAlert && user && messageOnPlatform && (
        <Row className="d-flex justify-content-center">
          <Col lg={9} md={8} sm={7} xs="auto" className='system-outage-alert'>
            <Alert severity="warning" color="warning" className="system-alert-box d-flex justify-content-center align-items-start" onClose={() => handleSystemAlertBoxClose()}>
              <AlertTitle className='system-alert-title'>System Alert</AlertTitle>
              {messageOnPlatform}
            </Alert>
          </Col>
        </Row>
      )}

      {
        (!loadingSite && user) &&
        <SessionTimeout
          extendSessionCallback={extendSession}
          sessionTimeoutPeriod={user.sessionTimeout}
        />
      }

    </React.Fragment>
  );
};

export default App;



// WEBPACK FOOTER //
// ./src/App.js